import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Building2, Landmark, Layers3, MapPinned } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { getCompany } from "../lib/api";
import { formatFy, getSectorChipClasses, normalizeGeographyLabel, normalizeImplementationModeLabel, normalizeSectorLabel } from "../lib/display";

const formatAmount = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Cr`;
const formatCount = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function CompanyProfilePage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectQuery, setProjectQuery] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const response = await getCompany(companyId);
        setCompany(response);
      } catch (loadError) {
        setError("We couldn't open this CSR company profile.");
      } finally {
        setLoading(false);
      }
    };

    loadCompany();
  }, [companyId]);

  const filteredProjects = useMemo(() => {
    if (!company?.projects?.length) {
      return [];
    }

    if (!projectQuery.trim()) {
      return company.projects;
    }

    const term = projectQuery.toLowerCase();
    return company.projects.filter((project) =>
      [
        project.fiscal_year,
        project.state,
        project.district_as_per_source,
        project.district_as_per_lgd,
        project.csr_project_name,
        project.sector,
        project.implementation_mode,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [company, projectQuery]);

  const summaryCards = company
    ? [
        { label: "Total spend", value: formatAmount(company.total_spend_crore), icon: Landmark },
        { label: "Total outlay", value: formatAmount(company.total_outlay_crore), icon: Layers3 },
        { label: "Districts covered", value: formatCount(company.district_count), icon: MapPinned },
        { label: "Project records", value: formatCount(company.project_count), icon: Building2 },
      ]
    : [];

  return (
    <div className="site-shell min-h-screen">
      <BrandHeader compact />

      <main className="mx-auto flex w-full max-w-[1700px] flex-col gap-8 px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:pt-10">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" data-testid="back-to-explorer-link">
            <Button type="button" variant="outline" className="rounded-none" data-testid="back-to-explorer-button">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to explorer
            </Button>
          </Link>
        </div>

        {loading ? (
          <section className="section-frame p-10 text-sm text-slate-500" data-testid="company-loading-state">
            Loading CSR company profile...
          </section>
        ) : null}

        {error ? (
          <section className="section-frame border-red-200 bg-red-50 p-10 text-sm text-red-700" data-testid="company-error-state">
            {error}
          </section>
        ) : null}

        {company ? (
          <>
            <section className="section-frame grid gap-8 p-6 md:grid-cols-[1.4fr_0.8fr] md:p-10" data-testid="company-profile-hero">
              <div className="space-y-5">
                <div className="hero-line" />
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">CSR company profile</p>
                <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl" data-testid="company-name-heading">
                  {company.company_name}
                </h1>
                <p className="max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base" data-testid="company-summary-copy">
                  This profile is built from the CIN-based CSR master dataset, so NGOs can review geography, implementation model, project names, and spend signals with company-identifier confidence.
                </p>

                <div className="flex flex-wrap gap-3">
                  <span className="border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800" data-testid="company-cin-badge">
                    CIN: {company.cin}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {summaryCards.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Card key={item.label} className="rounded-none border-slate-200 bg-white" data-testid={`company-stat-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <CardContent className="flex items-center justify-between gap-4 p-5">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">{item.label}</p>
                          <p className="mt-2 text-2xl font-black text-slate-950">{item.value}</p>
                        </div>
                        <div className="border border-slate-200 bg-slate-50 p-3 text-[#1E3A8A]">
                          <Icon size={20} />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>

            <section className="space-y-6">
              <Card className="section-frame rounded-none border-slate-200" data-testid="coverage-summary-card">
                <CardContent className="space-y-6 p-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Coverage snapshot</p>
                    <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Where this company is active.</h2>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="border border-slate-200 bg-slate-50 p-4" data-testid="coverage-strip-states">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">States covered</p>
                      <p className="mt-3 text-3xl font-black text-slate-950">{formatCount(company.state_count)}</p>
                    </div>

                    <div className="border border-slate-200 bg-slate-50 p-4" data-testid="coverage-strip-districts">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Districts covered</p>
                      <p className="mt-3 text-3xl font-black text-slate-950">{formatCount(company.district_count)}</p>
                    </div>

                    <div className="border border-slate-200 bg-slate-50 p-4" data-testid="coverage-strip-top-district">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Top district</p>
                      <p className="mt-3 text-lg font-black text-slate-950">{company.top_districts[0]?.label ? normalizeGeographyLabel(company.top_districts[0].label) : "Not specified"}</p>
                      <p className="mt-2 text-sm text-slate-500">{company.top_districts[0] ? `${formatAmount(company.top_districts[0].total_spend_crore)} • ${formatCount(company.top_districts[0].project_count)} records` : "No district concentration data"}</p>
                    </div>

                    <div className="border border-slate-200 bg-slate-50 p-4" data-testid="coverage-strip-top-sector">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Top sector</p>
                      <p className="mt-3 text-lg font-black text-slate-950">{company.top_sectors[0]?.label ? normalizeSectorLabel(company.top_sectors[0].label) : "Not specified"}</p>
                      <p className="mt-2 text-sm text-slate-500">{company.top_sectors[0] ? `${formatAmount(company.top_sectors[0].total_spend_crore)} • ${formatCount(company.top_sectors[0].project_count)} records` : "No sector concentration data"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="section-frame rounded-none border-slate-200" data-testid="source-fields-card">
                <CardContent className="space-y-5 p-6" data-testid="company-projects-panel">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Project history</p>
                      <p className="text-sm text-slate-500">Every source row is shown with all CSV fields clearly preserved.</p>
                    </div>
                    <Input
                      value={projectQuery}
                      onChange={(event) => setProjectQuery(event.target.value)}
                      placeholder="Filter this company's project history"
                      className="max-w-sm rounded-none border-slate-300"
                      data-testid="project-history-search-input"
                    />
                  </div>

                  <div className="overflow-x-auto border border-slate-200" data-testid="company-projects-table-wrapper">
                    <table className="min-w-full border-collapse text-left text-sm" data-testid="company-projects-table">
                      <thead className="bg-slate-950 text-white">
                        <tr>
                          <th className="px-4 py-3 font-semibold whitespace-nowrap">FY</th>
                          <th className="px-4 py-3 font-semibold">State</th>
                          <th className="px-4 py-3 font-semibold">District (Source)</th>
                          <th className="px-4 py-3 font-semibold">District (LGD)</th>
                          <th className="px-4 py-3 font-semibold">Project</th>
                          <th className="min-w-[220px] px-4 py-3 font-semibold">Sector</th>
                          <th className="px-4 py-3 font-semibold">Implementation Mode</th>
                          <th className="px-4 py-3 font-semibold">Outlay (Cr.)</th>
                          <th className="px-4 py-3 font-semibold">Spent (Cr.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProjects.map((project, index) => (
                          <tr key={`${project.fiscal_year}-${project.state}-${index}`} className="border-b border-slate-200 even:bg-slate-50">
                            <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatFy(project.fiscal_year)}</td>
                            <td className="px-4 py-3 text-slate-700">{normalizeGeographyLabel(project.state)}</td>
                            <td className="px-4 py-3 text-slate-700">{normalizeGeographyLabel(project.district_as_per_source)}</td>
                            <td className="px-4 py-3 text-slate-700">{normalizeGeographyLabel(project.district_as_per_lgd)}</td>
                            <td className="px-4 py-3 text-slate-700">{project.csr_project_name}</td>
                            <td className="min-w-[220px] px-4 py-3 text-slate-700">
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getSectorChipClasses(project.sector)}`}>
                                {normalizeSectorLabel(project.sector)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-700">{normalizeImplementationModeLabel(project.implementation_mode)}</td>
                            <td className="px-4 py-3 font-semibold text-slate-900">{Number(project.project_amount_outlay || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 font-semibold text-slate-900">{Number(project.amount_spent || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}